import React from 'react';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { ClientSubscriptionProcessingResult } from '@common/model/billing/clientSubscription';
import { useInjection } from '@cp/shared/di';
import { submitFormWithValues } from '@cp/utils/browser/submitForm';
import { isCancelled, useModalPromise } from '@cp/shared/modal';
import { useConfig } from '@cp/shared/config';
import { billingTariffApi } from '@cp/entities/tariff';
import { clientSubscriptionApi } from 'src/entities/subscription';
import { useIntentionService } from 'src/shared/intent';
import { useTrackEvent } from 'src/shared/tracking';
import { LOGIN_FEATURE_TOKEN } from '../di';
import { CHECKOUT_MODAL, PAYMENT_FAIL_MODAL, PAYMENT_SUCCESS_MODAL, TARIFF_SELECT_MODAL } from '../lib';
import { BillingEvents } from '../events';
import { createSubscriptionIntent } from './subscriptionIntent';
import { createPaymentResultIntent } from './paymentResultIntent';

/**
 * Subscription use-case entrypoint
 */
export const useStartBillingSubscription = () => {
  const login = useInjection(LOGIN_FEATURE_TOKEN);
  const isLoggedIn = useIsLoggedIn();
  const [loadSubscription] = clientSubscriptionApi.endpoints.getBillingSubscription.useLazyQuerySubscription();
  const [isLoading, setLoading] = React.useState(false);
  const selectTariff = useModalPromise(TARIFF_SELECT_MODAL);
  const checkout = useModalPromise(CHECKOUT_MODAL);
  const paymentSuccessModal = useModalPromise(PAYMENT_SUCCESS_MODAL);
  const paymentFailModal = useModalPromise(PAYMENT_FAIL_MODAL);
  const config = useConfig();
  const intentService = useIntentionService();
  const track = useTrackEvent();
  const [loadTariff] = billingTariffApi.endpoints.getBillingTariff.useLazyQuery();

  return [
    React.useCallback(async (): AsyncResult<ClientSubscriptionProcessingResult, Error> => {
      setLoading(true);
      const { data } = await loadSubscription();
      const tariffResult = await Result.fromAsync(() =>
        data && data?.tariff && !data.tariff.deletedAt ? selectTariff({ tariffId: data.tariff.id }) : selectTariff({}),
      );
      tariffResult.tapError(() => setLoading(false));

      if (isCancelled(tariffResult.error)) {
        return Result.Err<never, Error>(tariffResult.error);
      }
      const { tariffId } = tariffResult.unwrap();
      let tariff = await tariffResult.mapAsync(({ tariffId: id }) => loadTariff({ id }).unwrap());
      tariff = tariff.tap((tariffModel) => {
        track(BillingEvents.TariffSelect(tariffModel));
      });

      const subscribeIntent = createSubscriptionIntent();
      if (!isLoggedIn) {
        intentService.remember(subscribeIntent);
        const loginResult = await login({ tariffId });
        loginResult
          .tap(() => {
            intentService.clear();
          })
          .tapError(() => {
            setLoading(false);
          });
        if (loginResult.isErr) {
          return loginResult as Result<never, Error>;
        }
      }

      const successRedirectUrl = intentService.injectIntention(
        new URL(window.location.href, config.getOrThrow('CLIENT_WEB_ORIGIN')),
        createPaymentResultIntent(),
      );
      const failureRedirectUrl = intentService.injectIntention(
        new URL(window.location.href, config.getOrThrow('CLIENT_WEB_ORIGIN')),
        createPaymentResultIntent({ message: '' }), // we should inject a message on the backend
      );

      let shouldRetry = false;
      do {
        shouldRetry = false;
        
        let checkoutResult = await Result.fromAsync(
          () => checkout({ tariffId, successRedirectUrl, failureRedirectUrl }),
          (err) => (err instanceof Error ? err : new Error('Checkout error')),
        );
        checkoutResult
          .tap(() => {
            tariff.tap((tariffModel) => {
              track(BillingEvents.CheckoutModalSend(tariffModel));
            });
          })
          .tapError(() => setLoading(false));

        if (checkoutResult.isErr) {
          if (!isCancelled(checkoutResult.error)) {
            const failResult = await paymentFailModal({ error: checkoutResult.error, allowRetry: true });
            if (failResult.retry) {
              shouldRetry = true;
              continue;
            }
          }
          return checkoutResult;
        }

        checkoutResult = await checkoutResult.mapAsync(async (subscription) => {
          if (subscription.isPaymentMade) {
            await paymentSuccessModal({});
          } else if (subscription.acsRedirect) {
            const { url, params } = subscription.acsRedirect;
            submitFormWithValues(params, url, 'post');
          } else if (subscription.paymentRequired) {
            console.warn(subscription.error);
            const failResult = await paymentFailModal({ error: subscription.error, allowRetry: true });
            if (failResult.retry) {
              shouldRetry = true;
            }
          }
          return subscription;
        });
        
        if (shouldRetry) {
          continue;
        }
        
        return checkoutResult
          .tap(() => setLoading(false))
          .tapError(() => setLoading(false))
          .match<Result<ClientSubscriptionProcessingResult, Error>>({
            ok: (subscription) => (subscription.acsRedirect ? Result.Err(new Error('ACS Redirect')) : Result.Ok(subscription)),
            err: Result.Err,
          });
      } while (shouldRetry);
      
      setLoading(false);
      return Result.Err(new Error('Unexpected state'));
    }, [
      isLoggedIn,
      login,
      selectTariff,
      loadSubscription,
      checkout,
      paymentSuccessModal,
      paymentFailModal,
      config,
      intentService,
      track,
      loadTariff,
    ]),
    { isLoading },
  ] as const;
};
